/*----------------------------------
- CATALOG: PUBLIC PREVIEW COPY
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr ADR-0028
 *
 * The words the table, the phone cards and the phone hand-off put around the bounded preview.
 *
 * Every sentence here is checkable in the repo: the preview is capped at
 * `DATABASE_GUEST_RESULTS_LIMIT` rows in the engine's neutral order, and Full Access is the one
 * plan that searches past it (ADR-0083: Explorer, the free plan the body used to promise, is
 * retired). Counts are never typed here: the rows drawn and the search total fill the `{count}`
 * and `{total}` slots.
 */

/*----------------------------------
- CATALOG
----------------------------------*/

export const PUBLIC_PREVIEW_HANDOFF_COPY = {
    /** `{count}` is replaced with the number of rows actually drawn. */
    eyebrow: 'Public preview · {count} names',
    /** `{total}` is replaced with the search total, or the sentence falls back to `titleFallback`. */
    title: '{total} names in total, with filters and sorting.',
    titleFallback: 'Every name, with filters and sorting.',
    /**
     * `{count}` is the number of rows drawn, as in the eyebrow: Home's tab draws ten, `/domains*` at most
     * `DATABASE_GUEST_RESULTS_LIMIT` (review m-5: Home read "10 names" then "the first 20").
     */
    body: 'This preview shows the first {count} names in no particular order. Full Access searches every name with every filter and sort.',
    cta: 'See all results',
} as const;

/** Heading above the hub links the phone shows under the title. */
export const PUBLIC_PREVIEW_BROWSE_CHIPS_LABEL = 'Browse by';

/**
 * The words the table and the phone cards put around the cells (website-domains.html, 2026-10-25).
 * `{domain}` is the row's full name, `{filled}` and `{total}` the measured fill of a column on this preview.
 */
export const PUBLIC_PREVIEW_TABLE_COPY = {
    caption: 'Domain results',
    nameHead: 'Domain',
    actionsHead: 'Actions',
    /**
     * The journeys find a row's name and its "+" by "Open {domain}" and "Add {domain}". The name button is named by its
     * content, a hidden verb before the visible name (the glyph's letter is visible text, so an `aria-label` would not
     * contain it, WCAG 2.5.3); the "+" has no visible text and keeps its label.
     */
    openRowVerb: 'Open',
    addRow: 'Add {domain}',
    addRowTitle: 'Add to a watchlist',
    /** The phone card's strip of readings, a focusable group arrow keys scroll (review m-31, decision 101). */
    bandsLabel: 'Readings for {domain}',
    seeAllLead: 'See all',
    seeAllTail: 'domains',
    showing: 'Showing',
    showingOf: 'of',
    legend: { price: 'price', median: 'similar sales median', estimate: 'estimate' },
    /** The head hover card: the column's provenance, then how many rows of this preview carry it. */
    head: { what: 'What', from: 'From', refreshed: 'Refreshed', onPage: 'On this page', filled: 'Filled on {filled} of {total} rows' },
    spellingRisk: 'High spelling risk',
    /** The phone card's band for the rating, "Our read" as shipped. */
    ratingBand: 'Our read',
    renews: 'Renews',
    /** The rating's one-line hover: the band and its confidence, never the score (bands, not scores). */
    ratingHover: '{label} · {confidence} confidence',
} as const;

/** Fills a `{key}` template from the copy above with served values. */
export const fillPreviewCopy = (template: string, values: Record<string, string | number>): string =>
    template.replace(/\{(\w+)\}/g, (match, key: string) => (values[key] === undefined ? match : String(values[key])));
